import { useState } from "react";
import { toast } from "sonner";
import { Plus, Hand, Undo2, PackageCheck, CheckCircle2, XCircle, UserCog, History, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { SHOP_STATUS, PRIORITY, fmtDateTime, fmtQty, fmtCHF } from "@/lib/format";
import { MapBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, mapOpts as mapOptsSafe } from "@/components/Field";
import { ShoppingDialog } from "@/components/dialogs/ShoppingDialog";
import { PurchasedDialog } from "@/components/dialogs/PurchasedDialog";
import { MovementDialog } from "@/components/dialogs/MovementDialog";

const HIST = { created: "creato", taken: "preso in carico", released: "rilasciato", reassigned: "riassegnato", purchased: "acquistato", received: "ricevuto", cancelled: "annullato", edited: "modificato" };

const ItemCard = ({ it, me, can, act, onPurchased, onEdit, members }) => {
  const [showHist, setShowHist] = useState(false);
  const mine = it.taken_by === me;
  const approver = can("shopping", "approve");
  const author = it.created_by === me;
  return (
    <li data-testid={`shopping-item-${it.id}`} className="px-4 sm:px-5 py-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-slate-900">{it.name}</span>
            <span className="text-slate-700 font-medium">{fmtQty(it.quantity)} {it.unit}</span>
            {it.priority !== "normal" && <MapBadge map={PRIORITY} value={it.priority} />}
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            Richiesto da {it.created_by_name} · {fmtDateTime(it.created_at)}{it.event_name && ` · ${it.event_name}`}{it.store && ` · ${it.store}`}{it.budget != null && ` · budget ${fmtCHF(it.budget)}`}
          </div>
          {it.taken_by_name && <div className="text-sm text-slate-700 mt-0.5" data-testid={`shopping-item-taker-${it.id}`}>Lo compra: <strong>{it.taken_by_name}</strong> · {fmtDateTime(it.taken_at)}</div>}
          {it.status === "purchased" && <div className="text-sm text-slate-700">Acquistato{it.actual_cost != null && ` · ${fmtCHF(it.actual_cost)}`}{it.receipt && ` · scontrino ${it.receipt}`}</div>}
          {it.notes && <div className="text-sm text-slate-600 mt-1">{it.notes}</div>}
        </div>
        <MapBadge map={SHOP_STATUS} value={it.status} testId={`shopping-item-status-${it.id}`} />
      </div>
      <div className="flex flex-wrap gap-2">
        {it.status === "todo" && <Button data-testid={`shopping-take-${it.id}`} className="h-11 text-[15px] font-semibold" onClick={() => act(it, "take", "Preso in carico: lo compri tu")}><Hand className="h-4 w-4 mr-1" /> Lo compro io</Button>}
        {it.status === "taken" && (mine || approver) && <Button data-testid={`shopping-purchased-${it.id}`} className="h-11" onClick={() => onPurchased(it)}><CheckCircle2 className="h-4 w-4 mr-1" /> Ho acquistato</Button>}
        {it.status === "taken" && (mine || approver) && <Button data-testid={`shopping-release-${it.id}`} variant="outline" className="h-11" onClick={() => act(it, "release", "Articolo rilasciato")}><Undo2 className="h-4 w-4 mr-1" /> Rilascia</Button>}
        {it.status === "purchased" && approver && <Button data-testid={`shopping-received-${it.id}`} className="h-11" onClick={() => act(it, "received", "Ricezione confermata")}><PackageCheck className="h-4 w-4 mr-1" /> Conferma ricezione</Button>}
        {["todo", "taken"].includes(it.status) && approver && (
          <div className="w-48"><Sel testId={`shopping-reassign-${it.id}`} value="" onChange={(uid) => uid && act(it, "reassign", "Articolo riassegnato", { user_id: uid })} placeholder="Assegna a…" options={members.map((m) => ({ value: m.user_id, label: m.name }))} /></div>
        )}
        {["todo", "taken", "purchased"].includes(it.status) && (author || approver) && <Button data-testid={`shopping-edit-${it.id}`} variant="ghost" className="h-11" onClick={() => onEdit(it)}><Pencil className="h-4 w-4 mr-1" /> Modifica</Button>}
        {["todo", "taken"].includes(it.status) && (author || approver) && <Button data-testid={`shopping-cancel-${it.id}`} variant="ghost" className="h-11 text-red-700" onClick={() => act(it, "cancel", "Richiesta annullata")}><XCircle className="h-4 w-4 mr-1" /> Annulla</Button>}
        <Button data-testid={`shopping-history-${it.id}`} variant="ghost" className="h-11" onClick={() => setShowHist(!showHist)}><History className="h-4 w-4 mr-1" /> Storico</Button>
      </div>
      {showHist && (
        <ul className="text-sm text-slate-600 border-l-2 pl-3 space-y-1">
          {(it.history || []).map((h, i) => <li key={i}>{fmtDateTime(h.at)} · {h.by_name} ha {HIST[h.action] || h.action}{h.to && ` a ${h.to}`}</li>)}
        </ul>
      )}
    </li>
  );
};

export default function ShoppingPage() {
  const { user, can } = useAuth();
  const [view, setView] = useState("open");
  const [f, setF] = useState({ status: "", priority: "", taken_by: "", event_id: "" });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const qs = new URLSearchParams(Object.fromEntries(Object.entries({ view, ...f }).filter(([, v]) => v)));
  const { data, reload } = useFetch(`/shopping?${qs}`, [], { poll: 10000 });
  const members = useFetch("/members").data?.filter((m) => m.status === "active") || [];
  const events = useFetch("/events").data || [];

  const act = async (it, action, msg, body = {}) => {
    try {
      const { data: updated } = await api.post(`/shopping/${it.id}/${action}`, body);
      toast.success(msg);
      if (action === "received" && updated.product_id && can("movements", "create")) setReceiving(updated);
    } catch (e) {
      toast.error(formatError(e));
    }
    reload();
  };

  return (
    <>
      <PageHeader title="Lista della spesa" subtitle="Aggiornata automaticamente ogni pochi secondi">
        {can("shopping", "create") && <Button data-testid="shopping-add-button" className="h-11" onClick={() => setDialog(true)}><Plus className="h-4 w-4 mr-1" /> Aggiungi articolo</Button>}
      </PageHeader>
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="h-11">
          <TabsTrigger value="open" className="h-9 px-4" data-testid="shopping-tab-open">Da comprare e in corso</TabsTrigger>
          <TabsTrigger value="history" className="h-9 px-4" data-testid="shopping-tab-history">Storico</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Sel testId="shopping-filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Tutti gli stati"
          options={mapOptsSafe(SHOP_STATUS).filter((o) => (view === "open" ? ["todo", "taken", "purchased"] : ["received", "cancelled"]).includes(o.value))} />
        <Sel testId="shopping-filter-priority" value={f.priority} onChange={(v) => setF({ ...f, priority: v })} placeholder="Tutte le priorità" options={mapOptsSafe(PRIORITY)} />
        <Sel testId="shopping-filter-taker" value={f.taken_by} onChange={(v) => setF({ ...f, taken_by: v })} placeholder="Tutti i responsabili" options={members.map((m) => ({ value: m.user_id, label: m.name }))} />
        <Sel testId="shopping-filter-event" value={f.event_id} onChange={(v) => setF({ ...f, event_id: v })} placeholder="Tutti gli eventi" options={events.map((e) => ({ value: e.id, label: e.name }))} />
      </Card>
      {data && data.length === 0 ? <Empty testId="shopping-empty" text={view === "open" ? "Niente da comprare. Ottimo!" : "Nessun acquisto completato."} /> : (
        <Card><ul className="divide-y" data-testid="shopping-list">
          {data?.map((it) => <ItemCard key={it.id} it={it} me={user.id} can={can} act={act} members={members} onPurchased={setPurchasing} onEdit={setEditing} />)}
        </ul></Card>
      )}
      <ShoppingDialog open={dialog || !!editing} item={editing} onOpenChange={(o) => { if (!o) { setDialog(false); setEditing(null); } }} onSaved={reload} />
      <PurchasedDialog open={!!purchasing} item={purchasing} onOpenChange={(o) => !o && setPurchasing(null)} onSaved={reload} />
      <MovementDialog open={!!receiving} mode="in" shoppingItem={receiving} onOpenChange={(o) => !o && setReceiving(null)} onSaved={reload} />
    </>
  );
}
